/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { gridRowCount } from "../../../model/navigation/GridNavigation";
import type { TGridUnit, ContentGridMetrics } from "./ContentGridProps";

/**
 * Pure layout arithmetic for `ContentGrid` — kept out of the component so the
 * scroll/windowing rules can be unit-tested without rendering.
 */

/** Inclusive range of rows that are actually mounted. */
export type TRowWindow = {
  startRow: number;
  endRow: number;
};

/** Resolved, defaulted view of {@link ContentGridMetrics}. */
export type TResolvedMetrics = {
  itemWidth: string;
  itemHeight: string;
  /** See {@link ContentGridMetrics.contentWidth}. Defaults to `itemWidth`. */
  contentWidth: string;
  /** See {@link ContentGridMetrics.contentHeight}. Defaults to `itemHeight`. */
  contentHeight: string;
  columnGap: string;
  rowGap: string;
  /** Row pitch (item height + row gap) as a CSS length. */
  rowStep: string;
  /**
   * Row pitch and gap as bare numbers, plus their unit, so every offset the
   * component needs is multiplied out here rather than in a CSS `calc()`.
   * Keeping the arithmetic in one place also keeps the stylesheet free of
   * operators, which Prettier and stylelint cannot agree how to wrap.
   */
  rowStepValue: number;
  rowGapValue: number;
  unit: TGridUnit;
};

const DEFAULT_UNIT: TGridUnit = "vw";

/** Decimal places kept when multiplying out offsets. */
const LENGTH_PRECISION: number = 4;

/**
 * Formats a computed length, trimming binary floating-point noise
 * (`0.1 * 3` → `0.3`, not `0.30000000000000004`).
 */
export const toCssLength = (value: number, unit: TGridUnit): string => {
  const rounded: number =
    Math.round(value * Math.pow(10, LENGTH_PRECISION)) /
    Math.pow(10, LENGTH_PRECISION);
  return rounded + unit;
};

/** Applies defaults and stamps the CSS unit onto every metric. */
export const resolveMetrics = (
  metrics: ContentGridMetrics
): TResolvedMetrics => {
  const unit: TGridUnit =
    metrics.unit === undefined ? DEFAULT_UNIT : metrics.unit;
  const columnGap: number = metrics.columnGap || 0;
  const rowGap: number =
    metrics.rowGap === undefined ? columnGap : metrics.rowGap;
  const rowStepValue: number = metrics.itemHeight + rowGap;
  const contentWidth: number =
    metrics.contentWidth === undefined
      ? metrics.itemWidth
      : metrics.contentWidth;
  const contentHeight: number =
    metrics.contentHeight === undefined
      ? metrics.itemHeight
      : metrics.contentHeight;

  return {
    itemWidth: toCssLength(metrics.itemWidth, unit),
    itemHeight: toCssLength(metrics.itemHeight, unit),
    contentWidth: toCssLength(contentWidth, unit),
    contentHeight: toCssLength(contentHeight, unit),
    columnGap: toCssLength(columnGap, unit),
    rowGap: toCssLength(rowGap, unit),
    rowStep: toCssLength(rowStepValue, unit),
    rowStepValue,
    rowGapValue: rowGap,
    unit
  };
};

/**
 * Height of the clipped viewport: `visibleRows` worth of rows without the
 * trailing gap. Returns `undefined` when the grid is unbounded, so no height is
 * applied at all and the grid grows to its content.
 */
export const resolveViewportHeight = (
  resolved: TResolvedMetrics,
  visibleRows: number
): string | undefined => {
  if (visibleRows <= 0) {
    return undefined;
  }
  return toCssLength(
    resolved.rowStepValue * visibleRows - resolved.rowGapValue,
    resolved.unit
  );
};

/** Distance spanned by `rows` whole rows, as a CSS length. */
export const resolveRowOffset = (
  resolved: TResolvedMetrics,
  rows: number
): string => toCssLength(resolved.rowStepValue * rows, resolved.unit);

/**
 * Number of items laid out once `maxRows` is applied.
 * Items beyond the cap are dropped rather than clipped, so focus arithmetic and
 * rendering always agree on the item count.
 */
export const resolveRenderedItemCount = (
  itemCount: number,
  columns: number,
  maxRows?: number
): number => {
  if (itemCount <= 0 || columns <= 0) {
    return 0;
  }
  if (maxRows === undefined || maxRows <= 0) {
    return itemCount;
  }
  return Math.min(itemCount, maxRows * columns);
};

/**
 * Minimal-movement scroll: keeps `focusedRow` inside the visible window while
 * leaving the offset untouched whenever the row is already on screen. This is
 * what makes a grid feel anchored instead of re-centring on every move.
 *
 * @param previousScrollRow the offset currently applied.
 * @returns the top row that should be scrolled to.
 */
export const resolveScrollRow = (
  focusedRow: number,
  previousScrollRow: number,
  visibleRows: number,
  rowCount: number
): number => {
  if (rowCount <= 0 || visibleRows <= 0 || rowCount <= visibleRows) {
    return 0;
  }
  const maxScrollRow: number = rowCount - visibleRows;
  const clampedPrevious: number = Math.min(
    Math.max(previousScrollRow, 0),
    maxScrollRow
  );
  const safeFocusedRow: number = Math.min(
    Math.max(focusedRow, 0),
    rowCount - 1
  );

  if (safeFocusedRow < clampedPrevious) {
    return safeFocusedRow;
  }
  if (safeFocusedRow > clampedPrevious + visibleRows - 1) {
    return safeFocusedRow - visibleRows + 1;
  }
  return clampedPrevious;
};

/**
 * Inclusive row range to mount: the visible window grown by `overscanRows` on
 * each side and clamped to the data. Rows outside it are never rendered, which
 * is what keeps large grids affordable on SmartTV hardware.
 */
export const resolveRowWindow = (
  scrollRow: number,
  visibleRows: number,
  overscanRows: number,
  rowCount: number
): TRowWindow => {
  if (rowCount <= 0) {
    return { startRow: 0, endRow: -1 };
  }
  if (visibleRows <= 0) {
    return { startRow: 0, endRow: rowCount - 1 };
  }
  const startRow: number = Math.max(0, scrollRow - overscanRows);
  const endRow: number = Math.min(
    rowCount - 1,
    scrollRow + visibleRows - 1 + overscanRows
  );
  return { startRow, endRow };
};

/** Row count for a set of items — re-exported so callers need one import. */
export const rowCountFor = (itemCount: number, columns: number): number =>
  gridRowCount(itemCount, columns);
